<template>
  <div class="border border-gray-400/25 rounded-[3px] overflow-hidden">
    <div class="relative">
      <img
        ref="imageRef"
        :src="pageUrl"
        class="w-full pointer-events-none block transition-opacity"
        :class="{ 'opacity-30': props.selectedNumber !== null }"
        @load="onImageLoad"
      />

      <img
        v-for="cutout in cutouts"
        :key="cutout.key"
        :src="pageUrl"
        class="absolute top-0 left-0 w-full pointer-events-none block"
        :style="cutout.style"
      />

      <svg
        v-if="visibleOverlays.length"
        class="absolute top-0 left-0 w-full h-full overflow-visible"
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
      >
        <path
          v-for="overlay in visibleOverlays"
          :key="overlay.key"
          :d="overlay.path"
          class="cursor-pointer transition-colors [vector-effect:non-scaling-stroke]"
          :class="overlayStateClass(overlay)"
          @mouseenter="hoveredNumber = overlay.number"
          @mouseleave="hoveredNumber = null"
          @click.stop="emit('select', overlay.number)"
        />
      </svg>

      <MatchTag
        v-for="marker in visibleMarkers"
        :key="marker.key"
        :ref="(el) => setMarkerRef(marker.number, el)"
        :id="marker.number"
        :level="marker.level"
        clickable
        class="absolute -translate-x-2 -translate-y-2 z-10"
        :style="{ top: `${marker.top}%`, left: `${marker.left}%` }"
        @mouseenter="hoveredNumber = marker.number"
        @mouseleave="hoveredNumber = null"
        @click="emit('select', marker.number)"
      />
    </div>

    <Teleport to="body">
      <div
        v-if="selectedMatch && tooltipStyle"
        :style="tooltipStyle"
        class="pointer-events-auto"
        @click.stop
      >
        <MatchTooltip :match="selectedMatch" @view="onView" />
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import MatchTag from './MatchTag.vue'
import MatchTooltip from '../MatchTooltip.vue'
import { ScoreLevel } from '@/shared/graphql/router/graphql'
import { groupCoordsIntoBlocks, generateBlockPath } from '../../utils/coords'
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue'
import type { ComponentPublicInstance } from 'vue'
import type { PageMatchEntry } from './MatchesDocumentPage.vue'

const props = defineProps<{
  page: number
  pageUrl: string
  pageWidth?: number
  pageHeight?: number
  entries: PageMatchEntry[]
  selectedNumber?: number | null
}>()

const emit = defineEmits<{
  select: [number: number]
  deselect: []
  view: [matchId: string]
}>()

const imageRef = ref<HTMLImageElement | null>(null)
const imageWidth = ref(0)
const imageHeight = ref(0)
const hoveredNumber = ref<number | null>(null)

const coordinateWidth = computed(() => props.pageWidth || imageWidth.value)
const coordinateHeight = computed(() => props.pageHeight || imageHeight.value)

const overlays = computed(() => {
  if (!coordinateWidth.value || !coordinateHeight.value) return []

  const results: {
    key: string
    number: number
    path: string
    level?: ScoreLevel | null
  }[] = []

  for (const entry of props.entries) {
    const pageCoords = entry.match.coords.filter((c) => c.page === props.page)
    const blocks = groupCoordsIntoBlocks(pageCoords)

    blocks.forEach((block, blockIndex) => {
      const path = generateBlockPath(
        block,
        coordinateWidth.value,
        coordinateHeight.value,
        0.3,
        2
      )
      if (path) {
        results.push({
          key: `${entry.match.id}-${blockIndex}`,
          number: entry.number,
          path,
          level: entry.match.level,
        })
      }
    })
  }

  return results
})

const markers = computed(() => {
  if (!coordinateWidth.value || !coordinateHeight.value) return []

  const results: {
    key: string
    number: number
    level?: ScoreLevel | null
    top: number
    left: number
  }[] = []

  for (const entry of props.entries) {
    const pageCoords = entry.match.coords.filter((c) => c.page === props.page)
    if (!pageCoords.length) continue

    const topLeft = pageCoords.reduce((min, c) => (c.y1 < min.y1 ? c : min))

    results.push({
      key: entry.match.id,
      number: entry.number,
      level: entry.match.level,
      top: (topLeft.y1 / coordinateHeight.value) * 100,
      left: (topLeft.x1 / coordinateWidth.value) * 100,
    })
  }

  return results
})

const visibleOverlays = computed(() =>
  props.selectedNumber !== null && props.selectedNumber !== undefined
    ? overlays.value.filter((o) => o.number === props.selectedNumber)
    : overlays.value
)

const visibleMarkers = computed(() =>
  props.selectedNumber !== null && props.selectedNumber !== undefined
    ? markers.value.filter((m) => m.number === props.selectedNumber)
    : markers.value
)

const cutouts = computed(() => {
  if (
    props.selectedNumber == null ||
    !coordinateWidth.value ||
    !coordinateHeight.value
  ) {
    return []
  }
  const entry = props.entries.find((e) => e.number === props.selectedNumber)
  if (!entry) return []
  const pageCoords = entry.match.coords.filter((c) => c.page === props.page)
  return pageCoords.map((coord, i) => {
    const top = (coord.y1 / coordinateHeight.value) * 100
    const left = (coord.x1 / coordinateWidth.value) * 100
    const right = 100 - (coord.x2 / coordinateWidth.value) * 100
    const bottom = 100 - (coord.y2 / coordinateHeight.value) * 100
    return {
      key: `cutout-${i}`,
      style: {
        clipPath: `inset(${top}% ${right}% ${bottom}% ${left}%)`,
      },
    }
  })
})

const overlayStateClass = (overlay: {
  number: number
  level?: ScoreLevel | null
}) => {
  const isSelected = props.selectedNumber === overlay.number
  const isHovered = hoveredNumber.value === overlay.number

  if (isSelected) {
    return {
      'stroke-2': true,
      'stroke-gray-500 fill-gray-500/10': !overlay.level,
      'stroke-red-500 fill-red-500/10': overlay.level === ScoreLevel.High,
      'stroke-yellow-500 fill-yellow-500/10':
        overlay.level === ScoreLevel.Medium,
      'stroke-green-500 fill-green-500/10': overlay.level === ScoreLevel.Low,
    }
  }

  if (isHovered) {
    return {
      'stroke-0': true,
      'fill-gray-500/25': !overlay.level,
      'fill-red-500/25': overlay.level === ScoreLevel.High,
      'fill-yellow-500/25': overlay.level === ScoreLevel.Medium,
      'fill-green-500/25': overlay.level === ScoreLevel.Low,
    }
  }

  return {
    'stroke-0': true,
    'fill-gray-500/10': !overlay.level,
    'fill-red-500/10': overlay.level === ScoreLevel.High,
    'fill-yellow-500/10': overlay.level === ScoreLevel.Medium,
    'fill-green-500/10': overlay.level === ScoreLevel.Low,
  }
}

const onImageLoad = () => {
  if (!imageRef.value) return
  imageWidth.value = imageRef.value.naturalWidth
  imageHeight.value = imageRef.value.naturalHeight
}

const markerRefs = new Map<number, HTMLElement>()

const setMarkerRef = (
  number: number,
  el: Element | ComponentPublicInstance | null
) => {
  if (!el) {
    markerRefs.delete(number)
    return
  }
  const node = (el as ComponentPublicInstance).$el ?? (el as HTMLElement)
  markerRefs.set(number, node as HTMLElement)
}

const selectedEntry = computed(() =>
  props.selectedNumber == null
    ? null
    : (props.entries.find((entry) => entry.number === props.selectedNumber) ??
        null)
)

const selectedMatch = computed(
  () => selectedEntry.value?.match.timelineMatch ?? null
)

const selectedHasPageCoords = computed(() => {
  const entry = selectedEntry.value
  if (!entry) return false
  return entry.match.coords.some((c) => c.page === props.page)
})

const anchorRect = ref<{ x: number; top: number; bottom: number } | null>(null)

const TOOLTIP_W = 190
const TOOLTIP_H = 128
const TOOLTIP_GAP = 10

const updateAnchor = () => {
  if (
    !selectedHasPageCoords.value ||
    props.selectedNumber == null ||
    !imageRef.value ||
    !coordinateWidth.value ||
    !coordinateHeight.value
  ) {
    anchorRect.value = null
    return
  }
  const entry = selectedEntry.value
  if (!entry) {
    anchorRect.value = null
    return
  }
  const pageCoords = entry.match.coords.filter((c) => c.page === props.page)
  if (!pageCoords.length) {
    anchorRect.value = null
    return
  }
  const topMost = pageCoords.reduce((min, c) => (c.y1 < min.y1 ? c : min))
  const cxRatio = (topMost.x1 + topMost.x2) / 2 / coordinateWidth.value
  const topRatio = topMost.y1 / coordinateHeight.value
  const bottomRatio = topMost.y2 / coordinateHeight.value
  const imgRect = imageRef.value.getBoundingClientRect()
  const cx = imgRect.left + cxRatio * imgRect.width
  const top = imgRect.top + topRatio * imgRect.height
  const bottom = imgRect.top + bottomRatio * imgRect.height
  anchorRect.value = { x: cx, top, bottom }
}

watch(
  () => props.selectedNumber,
  async () => {
    await nextTick()
    updateAnchor()
  },
  { immediate: true }
)

const onScroll = () => updateAnchor()
const onResize = () => updateAnchor()

const onDocumentClick = () => {
  if (!selectedHasPageCoords.value) return
  emit('deselect')
}

onMounted(() => {
  window.addEventListener('scroll', onScroll, true)
  window.addEventListener('resize', onResize)
  document.addEventListener('click', onDocumentClick)
})

onBeforeUnmount(() => {
  window.removeEventListener('scroll', onScroll, true)
  window.removeEventListener('resize', onResize)
  document.removeEventListener('click', onDocumentClick)
})

const tooltipStyle = computed(() => {
  const a = anchorRect.value
  if (!a) return null
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1024
  const vh = typeof window !== 'undefined' ? window.innerHeight : 768
  const padding = 8

  let top = a.top - TOOLTIP_H - TOOLTIP_GAP
  if (top < padding) top = a.bottom + TOOLTIP_GAP
  let left = a.x - TOOLTIP_W / 2
  left = Math.max(padding, Math.min(vw - TOOLTIP_W - padding, left))
  top = Math.max(padding, Math.min(vh - TOOLTIP_H - padding, top))

  return {
    position: 'fixed' as const,
    left: `${left}px`,
    top: `${top}px`,
    width: `${TOOLTIP_W}px`,
    height: `${TOOLTIP_H}px`,
    zIndex: 60,
  }
})

const onView = () => {
  const entry = selectedEntry.value
  if (entry) emit('view', entry.match.id)
}
</script>
